import { memo, useCallback, useMemo } from 'react';
import { View } from 'react-native';
import Svg, { G, Path, Text } from 'react-native-svg';
import {
  CategoryTooltip,
  CategoryTooltipRow,
} from '../ComboChart/CategoryTooltip';
import { ComboTheme } from '../ComboChart/types';
import { FunnelStage, funnelLayout, kOverallWord, kStepWord } from './layout';

export interface FunnelChartProps {
  width: number;
  /** First stage first. */
  stages: FunnelStage[];
  showValues: boolean;
  showRates: boolean;
  theme: ComboTheme;
}

/**
 * A funnel: one trapezoid per stage, centered, as wide at its top as its
 * share of the largest and narrowing to the next stage's width; names on
 * the left, each stage's value on its shape and its rates (from the stage
 * before, from the first) on the right. Hovering or tapping a stage shows
 * its full name, value and rates, whichever labels the chart shows.
 */
export const FunnelChart = memo((props: FunnelChartProps) => {
  const { width, stages, showValues, showRates, theme } = props;
  const f = theme.fontSize;
  const layout = useMemo(
    () => funnelLayout({ width, stages, showValues, showRates, fontSize: f }),
    [width, stages, showValues, showRates, f],
  );
  const { band, shapeHeight, center, rows } = layout;
  const bands = useMemo(
    () => stages.map((_, i) => ({ x: 0, y: band * i, width, height: band })),
    [stages, band, width],
  );
  const names = useMemo(() => stages.map(stage => stage.name), [stages]);
  const rowsOf = useCallback(
    (i: number): CategoryTooltipRow[] => {
      const stage = stages[i];
      // The rates carry no series color: a clear dot keeps them aligned.
      const rate = (name: string, value: string | null) =>
        value === null ? [] : [{ color: 'transparent', name, value }];
      return [
        { color: stage.color, value: stage.valueText },
        ...rate(kStepWord, stage.stepText),
        ...rate(kOverallWord, stage.overallText),
      ];
    },
    [stages],
  );
  return (
    <View style={{ width, height: layout.height }}>
      <Svg width={width} height={layout.height}>
        {rows.map((row, i) => {
          const stage = stages[i];
          const middle = band * i + band / 2;
          const baseline = middle + f * 0.35;
          return (
            <G key={i}>
              <Text
                x={layout.nameX}
                y={baseline}
                fontSize={f}
                fontFamily={theme.fontFamily}
                fill={theme.axisTitle}
              >
                {row.name}
              </Text>
              {row.topWidth ? (
                <Path
                  d={[
                    `M${center - row.topWidth / 2},${row.y}`,
                    `L${center + row.topWidth / 2},${row.y}`,
                    `L${center + row.bottomWidth / 2},${row.y + shapeHeight}`,
                    `L${center - row.bottomWidth / 2},${row.y + shapeHeight}`,
                    'Z',
                  ].join(' ')}
                  fill={stage.color}
                />
              ) : null}
              {showValues ? (
                <Text
                  x={row.valueInside ? center : row.valueX}
                  y={baseline}
                  fontSize={f}
                  fontFamily={theme.fontFamily}
                  fontWeight="600"
                  fill={row.valueInside ? '#FFFFFF' : theme.label}
                  textAnchor={row.valueInside ? 'middle' : 'start'}
                >
                  {row.value}
                </Text>
              ) : null}
              {row.step ? (
                <Text
                  x={layout.ratesX}
                  y={row.overall ? middle - f * 0.2 : baseline}
                  fontSize={f}
                  fontFamily={theme.fontFamily}
                  fontWeight="600"
                  fill={theme.label}
                >
                  {row.step}
                </Text>
              ) : null}
              {row.overall ? (
                <Text
                  x={layout.ratesX}
                  y={middle + f * 1.05}
                  fontSize={f}
                  fontFamily={theme.fontFamily}
                  fill={theme.axisText}
                >
                  {row.overall}
                </Text>
              ) : null}
            </G>
          );
        })}
      </Svg>
      <CategoryTooltip
        width={width}
        height={layout.height}
        bands={bands}
        names={names}
        rowsOf={rowsOf}
        theme={theme}
      />
    </View>
  );
});
